import { badgeClaimRateParts } from 'features/claim/badge-claim-eligibility';
import type { RarityName } from 'features/catalog/rarity-config';

interface BadgeGroup {
  name: string;
  /** [anchor, accent] — high-contrast pairs; accent should pop early in the wash. */
  colors: string[];
  /** Where the accent color takes over (0–100). Lower = kicks in faster. */
  accentAt?: number;
  badgesIDs: string[];
}

export const slugifyGroupName = (name: string): string =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

/**
 * Diagonal wash with early accent. Anchors stay mid-saturated so fills aren't muddy-black.
 */
export const groupBackgroundGradient = (group: Pick<BadgeGroup, 'colors' | 'accentAt'>, angle = '125deg'): string => {
  const start = group.colors[0] ?? '#8b918c';
  const end = group.colors[1] ?? start;
  const accentAt = group.accentAt ?? 50;
  const hold = Math.max(0, accentAt * 0.35);
  return `linear-gradient(in oklab ${angle}, color-mix(in srgb, ${start} 78%, white) 0%, ${start} ${hold}%, ${end} ${accentAt}%, color-mix(in srgb, ${end} 70%, white) ${Math.min(100, accentAt + 28)}%, ${end} 100%)`;
};

export const BADGE_GROUPS: BadgeGroup[] = [
  {
    name: 'Tenure',
    // Heritage pine → polished jade
    colors: ['#245c4f', '#76c893'],
    badgesIDs: ['eaze', 'tpbo', '86xv', 'gu8b', 'nogh', 'eoak'],
  },
  {
    name: 'Paths Travelled',
    // Deep olive → soft sage
    colors: ['#4b673f', '#a8cc82'],
    // Intentional four-badge exception: these depend on uncommon career paths or opportunities.
    badgesIDs: ['intr', 'coi1', 'q6ss', 'ixey'],
  },
  {
    name: 'Passport Stamps',
    // Passport navy → ink-stamp crimson
    colors: ['#1e3a5f', '#c41e3a'],
    // Office check-ins + multi-office / travel milestones.
    badgesIDs: ['fk3y', 'ziyg', '56jo', 'zprr', 'qpwb', 'bost', '21yr', 'hpai', 'qpnd'],
  },
  {
    name: 'SRT & Badges',
    // Burnished amber → minted gold
    colors: ['#99651f', '#f0cd67'],
    // All Hands Timekeeper (`t4lx`) is an SRT event and must remain in this group.
    badgesIDs: ['x7tl', 'snp0', 'cxaa', 'pq4t', 'b4uv', 'srt1', 'srt2', 'srt3', 't4lx', '65ry', 'uwpa'],
  },
  {
    name: 'Community & Events',
    // Raspberry → peach sorbet
    colors: ['#a94f70', '#f3a08d'],
    badgesIDs: ['gm0p', 'rw0q', '52nh', 'rtdk', '9x0m', 'tk16', 'iuvn', 'fyzv', 'lark', '7ays', 'gcr2', 'htci'],
  },
  {
    name: 'Offsite Alumni',
    // Deep plum → visa magenta
    colors: ['#6b2d8b', '#d5006e'],
    // Venue stamps + offsite milestones
    badgesIDs: ['bc21', 'nc22', 'wf23', 'wl23', 'wl24', 'ag25', 'mx26', '3tgz', '5tgz'],
  },
  {
    name: 'Talent Pipeline',
    // Deep teal → fresh seafoam
    colors: ['#287a75', '#7bc9a7'],
    badgesIDs: ['n0w4', '1a0x', 'slg4', 'mgju', 'zuoo', 'c4mp'],
  },
  {
    name: 'Dealmakers & Referrals',
    // Executive black → deal-closing gold
    colors: ['#29282b', '#d7ad45'],
    badgesIDs: ['0lkm', 'bxbq', '6sj5', 'deal', 'seed'],
  },
  {
    name: 'Engineering & AI',
    // Cobalt → synthetic mint
    colors: ['#3659a6', '#4fd4bd'],
    badgesIDs: ['p12z', 'uv79', '2j0v', 'b2x1', 'mqpm', 'mmac', 'dr56', 'vib1', 'vib2', 'kmr1', 'tter', 'tele'],
  },
  {
    name: 'Operations & Support',
    // Operations navy → signal orange
    colors: ['#344d75', '#e58b55'],
    badgesIDs: ['jdoi', 'ayyc', 'a1y2', 'sy7t', '1mew', '4xjt'],
  },
  {
    name: 'In the Spotlight',
    // Orchid → stage-light gold
    colors: ['#824b91', '#e5b85d'],
    badgesIDs: ['mh96', '0gfr', 'hoeg', 'n26f', 'edgc', 'perf'],
  },
  {
    name: 'Gaming & Sports',
    // Arcade indigo → periwinkle
    colors: ['#514c9c', '#829fe5'],
    badgesIDs: [
      'jw0k',
      'l1ys',
      'gu53',
      'hl4y',
      'rt3p',
      'ao8z',
      '1m01',
      'cupp',
      'rac2',
      'jt0d',
      'fpl1',
      'pqup',
      'st3p',
      'bbcp',
      'bbtw',
      'xray',
    ],
  },
  {
    name: 'Trophy Cases',
    // Burnished bronze → trophy gold
    colors: ['#8b612d', '#ebc75f'],
    badgesIDs: ['99lz', 'j4l9', 'p5m0', 'ibdm', 'rac1', 'wcup', 'pquw', '62ex', '20nu'],
  },
  {
    name: 'Culinary Excellence',
    // Paprika → saffron cream
    colors: ['#a9533b', '#f1ad64'],
    badgesIDs: ['wg99', 'ifu1', 'ao3c', '3mjs', 'zqtm', 'c9da'],
  },
  {
    name: 'London Side Quests',
    // Union navy → heritage red
    colors: ['#293f73', '#c94f5d'],
    // Radical Rower (`c3gi`) is a London activity and must remain in this group.
    badgesIDs: ['t61t', 'lcof', 'lvrp', 'g0x1', 'bskt', 'c3gi'],
  },
];

/** All grouped badge IDs, precomputed for O(1) freeform membership checks. */
export const BADGE_GROUP_BADGE_IDS: ReadonlySet<string> = new Set(BADGE_GROUPS.flatMap((group) => group.badgesIDs));

type BadgeOwnershipSource = {
  participantIDsWhoOwnThisBadge?: string[];
  claimRateOwnerCount?: number;
  claimFloorOwnerCount?: number;
  eligibleClaimProfileCount?: number;
  claimUniverseProfileCount?: number;
  banSelfClaim?: boolean;
  rarityName?: RarityName;
};

/** Unique profile count inferred from badge ownership lists. */
export const totalProfilesFromBadgesMap = (badgesMap: Map<string, BadgeOwnershipSource>): number => {
  const owners = new Set<string>();
  for (const badge of badgesMap.values()) {
    for (const id of badge.participantIDsWhoOwnThisBadge ?? []) owners.add(id);
  }
  return owners.size;
};

/**
 * Average share of eligible profiles that own each badge in the group (0–1).
 * Per-badge rate matches the modal claim % (feed-visible updates after the
 * catalog drop, floored at engaged Claimed-By / engaged profiles). Badges with no rate yet
 * count as 0% so they pull the group down instead of being omitted.
 */
export const globalClaimRateForBadgeIds = (
  badgeIds: readonly string[],
  badgesMap: Map<string, BadgeOwnershipSource>,
  fallbackTotalProfiles: number
): number => {
  if (badgeIds.length === 0) return 0;
  let sum = 0;
  let counted = 0;
  for (const id of badgeIds) {
    const badge = badgesMap.get(id);
    if (!badge) continue;
    const rate = badgeClaimRateParts({
      id,
      participantIDsWhoOwnThisBadge: badge.participantIDsWhoOwnThisBadge ?? [],
      claimRateOwnerCount: badge.claimRateOwnerCount,
      claimFloorOwnerCount: badge.claimFloorOwnerCount,
      eligibleClaimProfileCount: badge.eligibleClaimProfileCount,
      claimUniverseProfileCount: badge.claimUniverseProfileCount ?? fallbackTotalProfiles,
      banSelfClaim: badge.banSelfClaim,
      rarityName: badge.rarityName,
    });
    sum += rate.rate;
    counted += 1;
  }
  return counted === 0 ? 0 : sum / counted;
};

export const globalClaimRateForGroup = (
  group: Pick<BadgeGroup, 'badgesIDs'>,
  badgesMap: Map<string, BadgeOwnershipSource>,
  fallbackTotalProfiles: number
): number => globalClaimRateForBadgeIds(group.badgesIDs, badgesMap, fallbackTotalProfiles);

/** Highest global claim-rate first; name tiebreak for stability. */
export const sortBadgeGroupsByGlobalClaimRate = <T extends Pick<BadgeGroup, 'name' | 'badgesIDs'>>(
  groups: readonly T[],
  badgesMap: Map<string, BadgeOwnershipSource>,
  totalProfiles = totalProfilesFromBadgesMap(badgesMap)
): T[] => {
  // Precompute rates once — comparator would otherwise recompute per comparison.
  const rateByName = new Map<string, number>();
  for (const group of groups) {
    rateByName.set(group.name, globalClaimRateForGroup(group, badgesMap, totalProfiles));
  }
  return [...groups].sort((a, b) => {
    const diff = (rateByName.get(b.name) ?? 0) - (rateByName.get(a.name) ?? 0);
    if (diff !== 0) return diff;
    return a.name.localeCompare(b.name);
  });
};

/**
 * Edit-profile group order: average claim % of only the caller-provided eligible
 * badge IDs (unclaimed + not hidden). Groups with no eligible badges sort last.
 */
export const sortBadgeGroupsByEligibleClaimRate = <T extends Pick<BadgeGroup, 'name' | 'badgesIDs'>>(
  groups: readonly T[],
  badgesMap: Map<string, BadgeOwnershipSource>,
  eligibleIdsByGroupName: Map<string, readonly string[]>,
  totalProfiles = totalProfilesFromBadgesMap(badgesMap)
): T[] => {
  const rateByName = new Map<string, number>();
  for (const group of groups) {
    const eligible = eligibleIdsByGroupName.get(group.name) ?? [];
    rateByName.set(
      group.name,
      eligible.length === 0 ? -1 : globalClaimRateForBadgeIds(eligible, badgesMap, totalProfiles)
    );
  }
  return [...groups].sort((a, b) => {
    const diff = (rateByName.get(b.name) ?? -1) - (rateByName.get(a.name) ?? -1);
    if (diff !== 0) return diff;
    return a.name.localeCompare(b.name);
  });
};

/** Collected/gallery section id for a badge — group slug or `freeform`. */
export const groupSlugForBadgeId = (badgeId: string): string => {
  const group = BADGE_GROUPS.find((g) => g.badgesIDs.includes(badgeId));
  return group ? slugifyGroupName(group.name) : 'freeform';
};

/** Claim-page group label for a badge — real group name or `More Badges`. */
export const groupNameForBadgeId = (badgeId: string): string => {
  const group = BADGE_GROUPS.find((g) => g.badgesIDs.includes(badgeId));
  return group ? group.name : 'More Badges';
};
